import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { FUNCTIONS_URL } from '../services/supabaseConfig';
import { Player } from '../types';

interface FreeAgencyPageProps {
  leagueId: string;
  teamId: string;
  onBack?: () => void;
}

type PositionFilter = 'ALL' | 'P' | 'C' | '1B' | '2B' | '3B' | 'SS' | 'OF' | 'UTIL';

type ViewMode = 'fa' | 'roster';

export default function FreeAgencyPage({ leagueId, teamId, onBack }: FreeAgencyPageProps) {
  const [faPlayers, setFaPlayers] = useState<Player[]>([]);
  const [roster, setRoster] = useState<Player[]>([]);
  const [budget, setBudget] = useState<number>(1000000);
  const [myBids, setMyBids] = useState<Map<string, any>>(new Map());
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [bidAmount, setBidAmount] = useState<number>(0);
  const [waivedPlayerId, setWaivedPlayerId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [positionFilter, setPositionFilter] = useState<PositionFilter>('ALL');
  const [faTimer, setFaTimer] = useState<number>(0);
  const [isFaActive, setIsFaActive] = useState(true);
  const [signings, setSignings] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('fa');

  // Load FA window timer
  useEffect(() => {
    const loadTimer = async () => {
      const { data: league } = await supabase.from('leagues').select('setup_window_end, status').eq('id', leagueId).single();
      if (league?.status === 'free_agency' && league?.setup_window_end) {
        const end = new Date(league.setup_window_end).getTime();
        const update = () => {
          const remaining = Math.max(0, Math.floor((end - Date.now()) / 1000));
          setFaTimer(remaining);
          if (remaining <= 0) setIsFaActive(false);
        };
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
      } else if (league?.status !== 'free_agency') {
        setIsFaActive(false);
      }
    };
    const cleanup = loadTimer();
    return () => { cleanup?.then(c => c?.()); };
  }, [leagueId]);

  useEffect(() => {
    loadData();
    // Poll for bid updates every 15s (blind — only see your own bids)
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [leagueId, teamId]);

  const loadData = async () => {
    await Promise.all([loadFaPlayers(), loadRoster(), loadBudget(), loadMyBids(), loadRecentSignings()]);
  };

  const loadFaPlayers = async () => {
    const { data } = await supabase
      .from('players')
      .select('*')
      .eq('league_id', leagueId)
      .is('team_id', null)
      .order('overall', { ascending: false });
    if (data) setFaPlayers(data as Player[]);
  };

  const loadRoster = async () => {
    const { data } = await supabase
      .from('players')
      .select('*')
      .eq('team_id', teamId)
      .order('position');
    if (data) setRoster(data as Player[]);
  };

  const loadBudget = async () => {
    const { data: team } = await supabase
      .from('teams')
      .select('fa_budget')
      .eq('id', teamId)
      .single();
    if (team) setBudget(team.fa_budget || 1000000);
  };

  const loadMyBids = async () => {
    const { data } = await supabase
      .from('fa_bids')
      .select('*')
      .eq('league_id', leagueId)
      .eq('team_id', teamId)
      .eq('status', 'pending');
    if (data) {
      const map = new Map<string, any>();
      data.forEach((b: any) => map.set(b.fa_player_id, b));
      setMyBids(map);
    }
  };

  const loadRecentSignings = async () => {
    const { data } = await supabase
      .from('fa_bids')
      .select('*, fa_player:players!fa_bids_fa_player_id_fkey(first_name, last_name, position), team:teams!fa_bids_team_id_fkey(name, city)')
      .eq('league_id', leagueId)
      .eq('status', 'won')
      .order('resolved_at', { ascending: false })
      .limit(20);
    if (data) setSignings(data);
  };

  const openBidModal = (player: Player) => {
    setSelectedPlayer(player);
    // If user already has a bid on this player, pre-fill it
    const existingBid = myBids.get(player.id!);
    setBidAmount(existingBid?.salary_offer || (player as any).salary_requirement || 50000);
    setWaivedPlayerId(existingBid?.waive_player_id || '');
    setMessage(null);
    setShowBidModal(true);
  };

  const submitBid = async () => {
    if (!selectedPlayer || !waivedPlayerId) {
      setMessage({ text: 'You must select a player to waive', type: 'error' });
      return;
    }
    if (bidAmount <= 0) {
      setMessage({ text: 'Bid must be positive', type: 'error' });
      return;
    }
    if (bidAmount > budget) {
      setMessage({ text: `Bid exceeds your budget ($${budget.toLocaleString()})`, type: 'error' });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setMessage({ text: 'Not authenticated', type: 'error' });
        setSubmitting(false);
        return;
      }

      const response = await fetch(`${FUNCTIONS_URL}/fa-bid-v2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          leagueId,
          faPlayerId: selectedPlayer.id,
          salaryOffer: bidAmount,
          waivePlayerId: waivedPlayerId,
        }),
      });

      const result = await response.json();
      if (result.error) {
        setMessage({ text: result.error, type: 'error' });
      } else {
        setMessage({ text: `✅ Bid submitted for ${result.faPlayer}! Waiving ${result.waivePlayer}. Blind — no one else can see your offer.`, type: 'success' });
        setShowBidModal(false);
        await loadData();
      }
    } catch (err: any) {
      setMessage({ text: err.message || 'Failed to submit bid', type: 'error' });
    }
    setSubmitting(false);
  };

  const cancelBid = async (bidId: string) => {
    await supabase.from('fa_bids').delete().eq('id', bidId);
    await loadMyBids();
  };

  const filteredFaPlayers = faPlayers.filter(p => {
    if (positionFilter !== 'ALL' && p.position !== positionFilter) return false;
    if (searchQuery && !`${p.firstName || ''} ${p.lastName || ''}`.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const formatSalary = (amount: number) => `$${amount.toLocaleString()}`;
  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const timerColor = faTimer > 300 ? '#22c55e' : faTimer > 60 ? '#f59e0b' : '#ef4444';

  return (
    <div className="free-agency-page">
      {/* HEADER with timer */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a, #1e293b)',
        borderBottom: `2px solid ${timerColor}`,
        padding: '16px 20px',
        textAlign: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: 4 }}>
          💰 Blind Free Agency — Bids are hidden from other owners
        </div>
        {isFaActive ? (
          <>
            <div style={{
              fontSize: '2.5rem', fontWeight: 700, fontFamily: 'monospace',
              color: timerColor, letterSpacing: '0.05em',
            }}>
              ⏱️ {formatTimer(faTimer)}
            </div>
            <div style={{ marginTop: 6 }}>
              <div style={{ height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.max(0, faTimer / 900 * 100)}%`, background: timerColor, transition: 'width 1s linear', borderRadius: 2 }} />
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 6 }}>
              Bids resolve every 60 seconds • Highest blind offer wins
            </div>
          </>
        ) : (
          <div style={{ fontSize: '1.5rem', color: '#9ca3af', fontWeight: 600 }}>
            🔒 Free Agency Closed
          </div>
        )}
      </div>

      {/* Budget bar */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 16px', background: 'rgba(59,130,246,0.08)',
        borderBottom: '1px solid rgba(59,130,246,0.2)',
      }}>
        <span style={{ color: '#93c5fd', fontSize: '0.9rem' }}>
          💵 Budget: <strong style={{ color: '#fff' }}>{formatSalary(budget)}</strong>
        </span>
        <span style={{ color: '#93c5fd', fontSize: '0.9rem' }}>
          📋 Your Bids: <strong style={{ color: '#fff' }}>{myBids.size}</strong>
        </span>
        <span style={{ color: '#93c5fd', fontSize: '0.9rem' }}>
          👥 Roster: <strong style={{ color: '#fff' }}>{roster.length}/25</strong>
        </span>
      </div>

      {/* View Toggle */}
      <div style={{ display: 'flex', gap: 0, margin: '8px 16px' }}>
        <button
          onClick={() => setViewMode('fa')}
          style={{
            flex: 1, padding: '8px 12px', border: 'none', cursor: 'pointer',
            background: viewMode === 'fa' ? '#2563eb' : '#1e293b', color: viewMode === 'fa' ? '#fff' : '#94a3b8',
            borderRadius: '8px 0 0 8px', fontWeight: 600, fontSize: '0.85rem',
          }}
        >🤝 Free Agents</button>
        <button
          onClick={() => setViewMode('roster')}
          style={{
            flex: 1, padding: '8px 12px', border: 'none', cursor: 'pointer',
            background: viewMode === 'roster' ? '#2563eb' : '#1e293b', color: viewMode === 'roster' ? '#fff' : '#94a3b8',
            borderRadius: '0 8px 8px 0', fontWeight: 600, fontSize: '0.85rem',
          }}
        >📋 My Roster</button>
      </div>

      {message && (
        <div style={{
          margin: '8px 16px', padding: '10px 14px', borderRadius: 8,
          background: message.type === 'success' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
          border: `1px solid ${message.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
          color: message.type === 'success' ? '#86efac' : '#fca5a5',
          fontSize: '0.85rem',
        }}>
          {message.text}
        </div>
      )}

      {/* FA View */}
      {viewMode === 'fa' && (<>
      {/* Your Active Bids */}
      {myBids.size > 0 && (
        <div style={{ padding: '8px 16px' }}>
          <h3 style={{ fontSize: '0.9rem', color: '#f59e0b', marginBottom: 8 }}>⏳ Your Pending Bids (hidden from others)</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Array.from(myBids.entries()).map(([playerId, bid]) => {
              const faPlayer = faPlayers.find(p => p.id === playerId);
              const waivePlayer = roster.find(p => p.id === bid.waive_player_id);
              return (
                <div key={playerId} style={{
                  background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)',
                  borderRadius: 8, padding: '8px 12px', fontSize: '0.8rem',
                }}>
                  <div style={{ color: '#fbbf24', fontWeight: 600 }}>
                    {faPlayer ? `${faPlayer.firstName} ${faPlayer.lastName}` : playerId}
                  </div>
                  <div style={{ color: '#9ca3af' }}>
                    Offer: {formatSalary(bid.salary_offer)} • Waive: {waivePlayer ? `${waivePlayer.firstName} ${waivePlayer.lastName}` : '?'}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    <button onClick={() => faPlayer && openBidModal(faPlayer)}
                      style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #f59e0b', background: 'transparent', color: '#f59e0b', cursor: 'pointer', fontSize: '0.75rem' }}>
                      Edit
                    </button>
                    <button onClick={() => cancelBid(bid.id)}
                      style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Signings (public — no salary shown) */}
      {signings.length > 0 && (
        <div style={{ padding: '8px 16px' }}>
          <h3 style={{ fontSize: '0.9rem', color: '#22c55e', marginBottom: 8 }}>✅ Recent Signings</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {signings.slice(0, 8).map((s: any) => (
              <div key={s.id} style={{
                background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)',
                borderRadius: 6, padding: '6px 10px', fontSize: '0.75rem', color: '#86efac',
              }}>
                {(s.fa_player as any)?.first_name} {(s.fa_player as any)?.last_name} → {(s.team as any)?.city} {(s.team as any)?.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ padding: '8px 16px', display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
        {(['ALL', 'P', 'C', '1B', '2B', '3B', 'SS', 'OF', 'UTIL'] as PositionFilter[]).map(pos => (
          <button key={pos}
            onClick={() => setPositionFilter(pos)}
            style={{
              padding: '4px 10px', borderRadius: 4,
              border: `1px solid ${positionFilter === pos ? '#3b82f6' : '#374151'}`,
              background: positionFilter === pos ? '#1e40af' : 'transparent',
              color: positionFilter === pos ? '#fff' : '#9ca3af',
              cursor: 'pointer', fontSize: '0.8rem',
            }}
          >{pos}</button>
        ))}
        <input type="text" placeholder="Search..." value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ marginLeft: 8, padding: '4px 10px', borderRadius: 4, border: '1px solid #374151', background: '#0f172a', color: '#fff', fontSize: '0.8rem', width: 120 }}
        />
        <span style={{ color: '#6b7280', fontSize: '0.75rem', marginLeft: 4 }}>
          {filteredFaPlayers.length} available
        </span>
      </div>

      {/* FA Player Grid */}
      <div style={{ padding: '4px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
        {filteredFaPlayers.map(player => {
          const hasBid = myBids.has(player.id!);
          const isPitcher = player.position === 'P';
          return (
            <div key={player.id} style={{
              background: hasBid ? 'rgba(245,158,11,0.08)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${hasBid ? 'rgba(245,158,11,0.3)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 8, padding: 12, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
              onClick={() => isFaActive && openBidModal(player)}
              onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'none')}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#fff' }}>
                  {player.firstName} {player.lastName}
                </span>
                {hasBid && <span style={{ fontSize: '0.7rem', color: '#f59e0b' }}>🔒 BID</span>}
              </div>
              <div style={{ display: 'flex', gap: 8, fontSize: '0.75rem', color: '#9ca3af', marginBottom: 6 }}>
                <span>{player.position}</span>
                <span style={{ color: player.overall >= 75 ? '#f59e0b' : player.overall >= 65 ? '#22c55e' : '#9ca3af', fontWeight: 600 }}>
                  OVR {player.overall}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6, fontSize: '0.7rem', color: '#6b7280' }}>
                {isPitcher ? (
                  <>
                    <span>VEL {player.attributes?.velocity || '-'}</span>
                    <span>CTL {player.attributes?.control || '-'}</span>
                    <span>DEC {player.attributes?.deception || '-'}</span>
                  </>
                ) : (
                  <>
                    <span>PWR {player.attributes?.power || '-'}</span>
                    <span>CON {player.attributes?.contact || '-'}</span>
                    <span>SPD {player.attributes?.speed || '-'}</span>
                  </>
                )}
              </div>
              {hasBid && (
                <div style={{ marginTop: 6, fontSize: '0.7rem', color: '#f59e0b' }}>
                  Your offer: {formatSalary(myBids.get(player.id!)?.salary_offer || 0)}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>)}

      {/* Roster View */}
      {viewMode === 'roster' && (
        <div style={{ padding: '0 16px' }}>
          <h3 style={{ color: '#e2e8f0', marginBottom: 12 }}>📋 My Roster ({roster.length}/25)</h3>

          {/* Roster holes summary */}
          {(() => {
            const posCounts: Record<string, number> = {};
            roster.forEach(p => { posCounts[p.position] = (posCounts[p.position] || 0) + 1; });
            const needed: Record<string, number> = { C: 2, '1B': 1, '2B': 1, '3B': 1, SS: 1, OF: 5, UTIL: 3, P: 10 };
            const holes: string[] = [];
            Object.entries(needed).forEach(([pos, need]) => {
              const have = posCounts[pos] || 0;
              if (have < need) holes.push(`${pos}: ${have}/${need}`);
            });
            if (roster.length < 25) holes.push(`Total: ${roster.length}/25`);
            return holes.length ? (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: '0.85rem', color: '#fca5a5' }}>
                ⚠️ <strong>Roster Holes:</strong> {holes.join(' · ')}
              </div>
            ) : (
              <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: '0.85rem', color: '#86efac' }}>
                ✅ <strong>Roster Full</strong> — All positions covered
              </div>
            );
          })()}

          {/* Hitters */}
          <h4 style={{ color: '#fbbf24', marginBottom: 8 }}>🏏 Hitters ({roster.filter(p => p.position !== 'P').length})</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
            {roster
              .filter(p => p.position !== 'P')
              .sort((a, b) => (b.overall || 50) - (a.overall || 50))
              .map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#1a1a2e', borderRadius: 6 }}>
                  <span style={{ color: '#94a3b8', width: 32, fontSize: '0.8rem' }}>{p.position}</span>
                  <span style={{ flex: 1, color: '#e2e8f0' }}>{p.firstName} {p.lastName}</span>
                  <span style={{ color: '#4ade80', fontWeight: 600, width: 30, textAlign: 'center' }}>{p.overall}</span>
                </div>
              ))}
          </div>

          {/* Pitchers */}
          <h4 style={{ color: '#60a5fa', marginBottom: 8 }}>⚾ Pitchers ({roster.filter(p => p.position === 'P').length})</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
            {roster
              .filter(p => p.position === 'P')
              .sort((a, b) => (b.overall || 50) - (a.overall || 50))
              .map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#1a1a2e', borderRadius: 6 }}>
                  <span style={{ color: '#94a3b8', width: 32, fontSize: '0.8rem' }}>{p.position}</span>
                  <span style={{ flex: 1, color: '#e2e8f0' }}>{p.firstName} {p.lastName}</span>
                  <span style={{ color: '#4ade80', fontWeight: 600, width: 30, textAlign: 'center' }}>{p.overall}</span>
                </div>
              ))}
          </div>

          <button
            onClick={() => setViewMode('fa')}
            style={{ padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
          >🤝 Back to Free Agents</button>
        </div>
      )}

      {/* Bid Modal */}
      {showBidModal && selectedPlayer && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 200, padding: 16,
        }} onClick={() => setShowBidModal(false)}>
          <div style={{
            background: '#1e293b', borderRadius: 16, padding: 24,
            maxWidth: 420, width: '100%', border: '1px solid rgba(255,255,255,0.1)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 16, color: '#fff' }}>🤝 Make a Blind Offer</h3>
            
            <div style={{ background: 'rgba(59,130,246,0.1)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, color: '#fff' }}>
                {selectedPlayer.firstName} {selectedPlayer.lastName}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                {selectedPlayer.position} | OVR {selectedPlayer.overall}
              </div>
              <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: 4 }}>
                👁️ No other owner can see your bid amount
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ color: '#9ca3af', fontSize: '0.85rem', display: 'block', marginBottom: 6 }}>
                Salary Offer
              </label>
              <input
                type="number"
                value={bidAmount}
                onChange={e => setBidAmount(parseInt(e.target.value) || 0)}
                min={50000}
                max={budget}
                step={10000}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #374151', background: '#0f172a', color: '#fff', fontSize: '1rem' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#6b7280', marginTop: 4 }}>
                <span>Min: $50,000</span>
                <span>Budget: {formatSalary(budget)}</span>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ color: '#9ca3af', fontSize: '0.85rem', display: 'block', marginBottom: 6 }}>
                🔻 Player to Waive (sign one → waive one)
              </label>
              <select
                value={waivedPlayerId}
                onChange={e => setWaivedPlayerId(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #374151', background: '#0f172a', color: '#fff', fontSize: '0.9rem' }}
              >
                <option value="">— Select player to waive —</option>
                {roster.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.firstName} {p.lastName} | {p.position} | OVR {p.overall}
                  </option>
                ))}
              </select>
              {waivedPlayerId && (() => {
                const wp = roster.find(p => p.id === waivedPlayerId);
                return wp ? (
                  <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4 }}>
                    ⚠️ {wp.firstName} {wp.lastName} will be released to FA pool if you win
                  </div>
                ) : null;
              })()}
            </div>

            {message && (
              <div style={{
                padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: '0.85rem',
                background: message.type === 'success' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                color: message.type === 'success' ? '#86efac' : '#fca5a5',
              }}>
                {message.text}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowBidModal(false)}
                style={{ flex: 1, padding: 12, borderRadius: 8, border: '1px solid #374151', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '0.9rem' }}>
                Cancel
              </button>
              <button onClick={submitBid} disabled={submitting || !waivedPlayerId}
                style={{
                  flex: 2, padding: 12, borderRadius: 8, border: 'none',
                  background: waivedPlayerId ? '#22c55e' : '#374151',
                  color: waivedPlayerId ? '#fff' : '#666',
                  cursor: waivedPlayerId ? 'pointer' : 'default',
                  fontSize: '0.9rem', fontWeight: 600,
                }}>
                {submitting ? 'Submitting...' : `🔒 Blind Offer ${formatSalary(bidAmount)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom spacer */}
      <div style={{ height: 40 }} />
    </div>
  );
}
